import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { whatsappQrCapabilities } from "../../utils/whatsappQrCapabilities";
import WhatsappQrPairingSection from "./WhatsappQrPairingSection";
import WhatsappQrProfileSection from "./WhatsappQrProfileSection";
import WhatsappQrConnectionVersionSection from "./WhatsappQrConnectionVersionSection";

export default function WhatsappQrSetupPanel({
  version,
  hasSession,
  channelId,
  statusLabel,
  statusColor = "default",
  statusHint,
  loading = false,
  canCreateInstance = false,
  onCreateInstance,
  canShowQr = false,
  onShowQr,
  qrSrc,
  qrExpiresAt,
  onRenewQr,
  webhookUrl,
  rejeitarChamadas,
  chamadasAtivo,
  onChamadasAtivo,
  onRejeitarChamadas,
  onSaveCalls,
  callsSaving = false,
  callsMessage,
  callsSeverity = "info",
  onVersionSwitched,
}) {
  const { t } = useTranslation("settings");
  const caps = whatsappQrCapabilities(version);
  const [connectMode, setConnectMode] = useState(null);
  const [extraAction, setExtraAction] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!qrExpiresAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [qrExpiresAt]);

  const qrRemaining = qrExpiresAt ? Math.max(0, Math.ceil((qrExpiresAt - now) / 1000)) : 0;
  const qrExpired = Boolean(qrExpiresAt && now > qrExpiresAt);

  const extras = useMemo(() => {
    const items = [
      { id: "version", label: t("channelForm.waQr.actionVersion", { defaultValue: "Versão da conexão" }) },
      { id: "calls", label: t("channelForm.waQr.actionCalls", { defaultValue: "Chamadas" }) },
    ];
    if (caps.accountProfile) {
      items.splice(1, 0, {
        id: "profile",
        label: t("channelForm.waQr.actionProfile", { defaultValue: "Perfil da conta" }),
      });
    }
    return items;
  }, [caps.accountProfile, t]);

  const toggleExtra = (id) => setExtraAction((prev) => (prev === id ? null : id));
  const toggleConnect = (mode) => setConnectMode((prev) => (prev === mode ? null : mode));

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {t("channelForm.waQr.setupTitle", { defaultValue: "Conectar o WhatsApp" })}
        </Typography>
        {statusLabel ? <Chip size="small" label={statusLabel} color={statusColor} /> : null}
      </Stack>
      {statusHint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {statusHint}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("channelForm.waQr.setupIntro", {
            defaultValue:
              "Primeiro crie a sessão deste canal. Depois escolha como o celular vai entrar: QR Code ou código de 8 dígitos.",
          })}
        </Typography>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          disabled={loading || !canCreateInstance}
          onClick={onCreateInstance}
        >
          {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          {t("channelForm.waQr.createInstance", { defaultValue: "Criar instância" })}
        </Button>
        <Button
          variant={connectMode === "qr" ? "contained" : "outlined"}
          disabled={loading || !hasSession}
          onClick={() => toggleConnect("qr")}
        >
          {t("channelForm.waQr.actionQr", { defaultValue: "QR Code" })}
        </Button>
        <Button
          variant={connectMode === "pairing" ? "contained" : "outlined"}
          disabled={loading || !hasSession}
          onClick={() => toggleConnect("pairing")}
        >
          {t("channelForm.waQr.actionPairing", { defaultValue: "Código no celular" })}
        </Button>
      </Stack>

      {connectMode === "qr" ? (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("channelForm.waQr.qrHelp", {
              defaultValue:
                "No celular: WhatsApp → Aparelhos conectados → Conectar um aparelho. Escaneie o código abaixo.",
            })}
          </Typography>
          <Button variant="outlined" disabled={loading || !canShowQr} onClick={onShowQr} sx={{ mb: 1.5 }}>
            {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            {qrSrc
              ? t("channelForm.waQr.refreshQr", { defaultValue: "Atualizar QR Code" })
              : t("channelForm.waQr.showQr", { defaultValue: "Mostrar QR Code" })}
          </Button>
          {qrSrc ? (
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <Box
                  component="img"
                  src={qrSrc}
                  alt="QR Code WhatsApp"
                  sx={{
                    maxWidth: 260,
                    maxHeight: 260,
                    width: "100%",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    filter: qrExpired ? "grayscale(1) opacity(0.4)" : "none",
                  }}
                />
                {!qrExpired && qrExpiresAt ? (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                      bgcolor: "rgba(0,0,0,0.7)",
                      color: "#fff",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: 12,
                    }}
                  >
                    {t("channelForm.waQr.qrExpires", {
                      defaultValue: "Expira em {{seconds}}s",
                      seconds: qrRemaining,
                    })}
                  </Box>
                ) : null}
                {qrExpired ? (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,0,0,0.55)",
                      borderRadius: 2,
                    }}
                  >
                    <Button variant="contained" size="small" onClick={onRenewQr || onShowQr}>
                      {t("channelForm.waQr.refreshQr", { defaultValue: "Atualizar QR Code" })}
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" display="block">
              {t("channelForm.waQr.qrEmpty", {
                defaultValue: "Clique em Mostrar QR Code. Se a sessão já estiver online, não é preciso escanear de novo.",
              })}
            </Typography>
          )}
        </Box>
      ) : null}

      {connectMode === "pairing" ? (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
          <WhatsappQrPairingSection channelId={channelId} hasSession={hasSession} />
        </Box>
      ) : null}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {t("channelForm.waQr.moreActions", { defaultValue: "Outras ações — abra só o que precisar" })}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {extras.map((item) => (
          <Button
            key={item.id}
            size="small"
            variant={extraAction === item.id ? "contained" : "outlined"}
            onClick={() => toggleExtra(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </Stack>

      {extraAction === "version" ? (
        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
          <WhatsappQrConnectionVersionSection
            channelId={channelId}
            savedVersion={version}
            hasSession={hasSession}
            webhookUrl={webhookUrl}
            rejeitarChamadas={rejeitarChamadas}
            onSwitched={onVersionSwitched}
          />
        </Box>
      ) : null}

      {extraAction === "profile" && caps.accountProfile ? (
        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
          <WhatsappQrProfileSection channelId={channelId} version={version} />
        </Box>
      ) : null}

      {extraAction === "calls" ? (
        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("channelForm.waQr.callsHelp", {
              defaultValue:
                "O canal não atende chamada. Você só pode ver o toque no chat e recusar automaticamente.",
            })}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(chamadasAtivo)}
                onChange={(e) => onChamadasAtivo?.(e.target.checked)}
              />
            }
            label={t("channelForm.waQr.callsNotify", { defaultValue: "Avisar toques no chat" })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(rejeitarChamadas)}
                onChange={(e) => onRejeitarChamadas?.(e.target.checked)}
              />
            }
            label={t("channelForm.waQr.callsReject", { defaultValue: "Recusar chamadas automaticamente" })}
          />
          <Box sx={{ mt: 1 }}>
            <Button variant="outlined" disabled={callsSaving || !channelId} onClick={onSaveCalls}>
              {callsSaving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              {t("channelForm.waQr.saveCalls", { defaultValue: "Salvar chamadas" })}
            </Button>
          </Box>
          {callsMessage ? (
            <Alert severity={callsSeverity} sx={{ mt: 1 }}>
              {callsMessage}
            </Alert>
          ) : null}
        </Box>
      ) : null}
    </Card>
  );
}
